import type { Program, ProgramActivityContentObjectList } from 'models';
import { can } from 'old/utils/permissions';
import { useNavigate } from 'react-router';
import { Button, Col, Image, RichTextRenderer, Row, Text, Title } from 'ui';
import { isDexcomEnabled } from 'common/validators';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Not re-exported from the features/programs barrel's public surface with a matching module path for the characterization test's mock
import ActivityTask from 'features/programs/feed/ActivityTask';
import type { PeriodicityType } from 'models/activity';

type P = {
  dexcomSynced: boolean;
  item: ProgramActivityContentObjectList;
  program: Program;
};

const DexcomSync = () => {
  const navigate = useNavigate();

  return (
    <div className="modules-filter__synchronized-wrapper">
      <Row gutter={[0, 'ms']}>
        <Col span={24}>
          <Image preview={false} src="/assets/dexcom.png" />
        </Col>
        <Col span={24}>
          <Title level={2}>How to set up your device and account</Title>
        </Col>
        <Col span={24}>
          <Text>
            1. Watch the video tutorial below on how to create your Dexcom account and pair the device to this account.
          </Text>
        </Col>
        <Col span={24}>
          <RichTextRenderer data='<figure class="media"><oembed url="https://www.youtube.com/watch?v=IMq-M5sdpJ8"></oembed></figure>' />
        </Col>
        <Col span={24}>
          <Text>
            2. After your account is created and your device is paired, click the &quot;Synchronize account&quot; button
            below.Follow the prompts to log in to your Dexcom account and sign the HIPAA authorization.
          </Text>
        </Col>
      </Row>

      <Button
        className="topic-item__order"
        onClick={() => {
          // eslint-disable-next-line sonarjs/void-use -- We dont want to wait for this navigate
          void navigate('/settings-new?scope=dexcom-sync', {
            state: { automaticSync: true, backUrl: window.location.pathname },
          });
        }}
        size="large"
        type="primary"
      >
        Synchronize account
      </Button>
    </div>
  );
};

/**
 * @deprecated AI-generated code, not reviewed. Needs revision before further use — do not use as a reference or pattern to follow.
 */
const ActivityContent = ({ dexcomSynced, item, program }: P) => {
  const activity = {
    ...item.activity,
    description: item.activity.description ?? '',
    htmlDescription: item.activity.htmlDescription ?? '',
    nextPeriodDate: item.activity.nextPeriodDate ?? null,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Backend documents periodicity_type as a plain string; ActivityTask needs the narrower literal union
    periodicityType: item.activity.periodicityType as PeriodicityType,
    programsTopicId: item.programsTopic.id,
    times: item.activity.times ?? [],
  };
  const shouldRenderDexcomSync =
    isDexcomEnabled() && activity.isDexcom && !dexcomSynced && !can(item.program, 'Program', 'contentCreator');

  if (shouldRenderDexcomSync) {
    return (
      <div>
        <DexcomSync />
      </div>
    );
  }

  return (
    <div className="topic-item__activity">
      <div className="topic-item__activity-tasks">
        <ActivityTask
          activity={activity}
          canDownloadReport={Boolean(can(program, 'Program', 'downloadReport'))}
          canSubmitData={Boolean(can(activity, 'Activity', 'submitData'))}
          isProgramPublished={program.status === 'published'}
          // eslint-disable-next-line @typescript-eslint/require-await -- ActivityTask's onSuccess prop requires an async function
          onSuccess={async () => undefined}
          reloadingPost={false}
          userSubscribed={Boolean(can(item.program, 'Program', 'joinedAsUser'))}
        />
      </div>
    </div>
  );
};

export default ActivityContent;
